import { useCallback, useEffect, useMemo, useState } from "react";
import { Compass, Lock, RotateCcw } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import TabPage, { TabItemProps } from "../../components/dynamic/Tab";
import { ApiService } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import CoachOnboarding from "./CoachOnboarding";
import CoachChat from "./CoachChat";
import CoachToday from "./CoachToday";
import CoachPlan from "./CoachPlan";
import Roadmap from "./Roadmap";
import Defterim from "./Defterim";
import CoachLetters from "./CoachLetters";
import Kitaplarim from "./Kitaplarim";
import type { NavigatorLessonStatus } from "../../types/navigator";

// Koçum — plan koçu. Öğrenci buraya "ne yapacağım?" diye gelir.
// Sıra: tanışma yoksa tanış → varsa bugünün işleri.
// Öğretme AI öğretmende, ölçme E-Eğitim test modunda; burası sıra ve zaman yönetir.

function KoclukKilidi() {
  return (
    <div className="mx-auto max-w-xl p-4 sm:p-6">
      <div className="rounded-xl border border-gray-200 bg-white p-6 text-center">
        <Lock className="mx-auto h-7 w-7 text-gray-400" />
        <h2 className="mt-2 text-base font-semibold text-gray-900">Plan ve hedef paketle açılır</h2>
        <p className="mt-1 text-sm text-gray-600">Kitaplarım ücretsiz. Günlük plan, yol haritası ve koç mektupları için bir paket seç.</p>
        <Link to={`/payment?module=lessons&returnTo=${encodeURIComponent("/navigator")}`} className="mt-4 inline-flex rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
          Paketleri gör
        </Link>
      </div>
    </div>
  );
}

export default function Navigator() {
  const { user } = useAuth();
  // Kitap formunda sabit düğme satırların üstüne binmesin
  const kitapSekmesinde = new URLSearchParams(useLocation().search).get("tab") === "kitaplar";
  // Erişim burada taze okunur: girişten sonra boş ya da süresi dolmuş deneme kalmış olabilir,
  // kilit görülmeden çağrılan plan uçları 402 alıp öğrenciyi ödeme sayfasına atar.
  const [erisim, setErisim] = useState<any>(undefined);
  useEffect(() => {
    let alive = true;
    ApiService.getPaymentAccess().catch(() => null).then((data) => {
      if (alive) setErisim(data);
    });
    return () => {
      alive = false;
    };
  }, []);
  const erisimHazir = erisim !== undefined;
  // Kitaplarım ücretsiz: E-Eğitim ödemesi bekleyen öğrenci Koçum'a girer, plan ve hedef kilitli kalır.
  const koclukKilitli = (erisim || (user as any)?.paymentAccess)?.modules?.find((module: any) => module.id === "lessons")?.access === "PAYMENT_REQUIRED";
  const [hasPlan, setHasPlan] = useState<boolean | null>(null);
  const [lessons, setLessons] = useState<NavigatorLessonStatus[]>([]);
  const [resetting, setResetting] = useState(false);
  const [kitaplarAcik, setKitaplarAcik] = useState(false);
  // Koç sohbetinden plan revize edilince Bugün/Planım sekmeleri yeniden kurulsun diye artar.
  const [planVersion, setPlanVersion] = useState(0);
  // Tanışma sıfırlanınca ekranı YENİDEN kur: profil silindiği için bileşen taze veriyle açılmalı
  // (yoksa sohbetin eski mesajları ekranda kalıyordu).
  const [onboardingVersion, setOnboardingVersion] = useState(0);

  const check = useCallback(async () => {
    try {
      const today = await ApiService.getCoachToday();
      setHasPlan(today.hasPlan);
    } catch {
      setHasPlan(false);
    }
  }, []);

  useEffect(() => {
    if (erisimHazir && !koclukKilitli) check();
  }, [check, erisimHazir, koclukKilitli]);

  // Defterim ders listesine ihtiyaç duyuyor — plan varken arka planda çek.
  useEffect(() => {
    if (!hasPlan || koclukKilitli) return;
    let alive = true;
    (async () => {
      try {
        const res = await ApiService.getNavigatorStatus();
        if (alive) setLessons(res.lessons || []);
      } catch {
        /* geç */
      }
    })();
    return () => {
      alive = false;
    };
  }, [hasPlan, koclukKilitli]);

  const restart = async () => {
    const soru = hasPlan
      ? "Koçunla baştan tanışacaksın ve yeni bir plan kurulacak. Devam edilsin mi?"
      : "Tanışmayı baştan başlatacaksın; şu ana kadar verdiğin cevaplar silinecek. Devam edilsin mi?";
    if (!window.confirm(soru)) return;
    setResetting(true);
    try {
      await ApiService.resetCoachProfile();
      setOnboardingVersion((v) => v + 1);
      setHasPlan(false);
    } finally {
      setResetting(false);
    }
  };

  const kitapSekmesi = useMemo<TabItemProps>(
    () => ({ type: "custom" as const, title: "Kitaplarım", path: "coach-kitaplar", tabId: "kitaplar", customComponent: <Kitaplarim /> }),
    []
  );

  const tabs: TabItemProps[] = useMemo(() => {
    if (koclukKilitli) {
      return [kitapSekmesi, { type: "custom" as const, title: "Plan ve Hedef", path: "coach-kilit", tabId: "kilit", customComponent: <KoclukKilidi /> }];
    }
    if (!hasPlan) return [];
    return [
      {
        type: "custom" as const,
        title: "Bugün",
        path: "coach-today",
        tabId: "today",
        customComponent: <CoachToday key={`today-${planVersion}`} onNeedOnboarding={() => setHasPlan(false)} />,
      },
      {
        type: "custom" as const,
        title: "Yol Haritam",
        path: "coach-roadmap",
        tabId: "roadmap",
        // key yok: sekme zaten geçişte yeniden kurulur; plan kurulunca kendi verisini yeniler,
        // key değişseydi "Planın yenilendi" satırı kaybolurdu.
        customComponent: <Roadmap onPlanRebuilt={() => setPlanVersion((v) => v + 1)} />,
      },
      {
        type: "custom" as const,
        title: "Koçla Konuş",
        path: "coach-chat",
        tabId: "chat",
        customComponent: <CoachChat onPlanRevised={() => setPlanVersion((v) => v + 1)} />,
      },
      {
        type: "custom" as const,
        title: "Planım",
        path: "coach-plan",
        tabId: "plan",
        customComponent: <CoachPlan key={`plan-${planVersion}`} />,
      },
      { ...kitapSekmesi, customComponent: <Kitaplarim planAcik /> },
      {
        type: "custom" as const,
        title: "Mektuplar",
        path: "coach-letters",
        tabId: "letters",
        customComponent: <CoachLetters />,
      },
      {
        type: "custom" as const,
        title: "Defterim",
        path: "coach-defterim",
        tabId: "defterim",
        customComponent: <Defterim lessons={lessons} />,
      },
    ];
  }, [hasPlan, kitapSekmesi, koclukKilitli, lessons, planVersion]);

  if (koclukKilitli) return <TabPage title="Koçum" tabs={tabs} />;

  if (hasPlan === null) {
    return (
      <div className="grid min-h-[calc(100dvh-4rem)] place-items-center bg-gray-50">
        <div className="h-9 w-9 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!hasPlan) {
    return (
      <div className="min-h-[calc(100dvh-4rem)] bg-gray-50">
        {kitaplarAcik && (
          <>
            <div className="mx-auto max-w-3xl px-4 pt-4 sm:px-6">
              <button type="button" onClick={() => { setKitaplarAcik(false); check(); }} className="text-sm font-medium text-blue-600 hover:underline">Koçunla tanışmaya dön</button>
            </div>
            <Kitaplarim />
          </>
        )}
        {/* Tanışma gizlenir ama kurulu kalır: cevaplar ve süren gönderim kaybolmasın */}
        <div className={kitaplarAcik ? "hidden" : ""}>
        <div className="mx-auto max-w-2xl px-4 pt-5 sm:px-6">
          <div className="mb-4 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
              <Compass className="h-5 w-5 text-blue-600" /> Koçum
            </div>
            {/* Tanışma tıkanırsa (koç dönüp duruyor, sohbet hata veriyor) tek çıkış kapısı bu.
                Önceden yalnız planı olanlara gösteriliyordu; tanışmada kalan öğrenci sıkışıyordu. */}
            <button
              onClick={restart}
              disabled={resetting}
              className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:text-blue-700 disabled:opacity-60"
              title="Tanışmayı baştan başlat"
            >
              <RotateCcw className={`h-3.5 w-3.5 ${resetting ? "animate-spin" : ""}`} />
              Baştan tanış
            </button>
          </div>
        </div>
        <CoachOnboarding key={`onboarding-${onboardingVersion}`} onReady={check} />
        <div className="mx-auto max-w-2xl px-4 pb-8 sm:px-6">
          <button type="button" onClick={() => setKitaplarAcik(true)} className="w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-gray-700 hover:border-blue-300">
            Kitaplarım: tanışmadan önce de kitaplarını ekleyebilirsin
          </button>
        </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <TabPage title="Koçum" tabs={tabs} />
      {!kitapSekmesinde && <button
        onClick={restart}
        disabled={resetting}
        className="fixed bottom-4 right-4 z-30 inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-600 shadow-lg transition-colors hover:text-blue-700 disabled:opacity-60"
        title="Hedefin değiştiyse koçunla baştan tanış"
      >
        <RotateCcw className={`h-3.5 w-3.5 ${resetting ? "animate-spin" : ""}`} />
        Baştan tanış
      </button>}
    </div>
  );
}
